/**
 * NEXORAX SOCIAL MEDIA AUTOMATION
 * High-fidelity Purple Glassmorphism SaaS Suite
 * Plan. Create. Schedule. Publish. Grow.
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  Sparkles,
  Youtube,
  Instagram,
  Linkedin,
  FileText,
  Clock,
  TrendingUp,
  Cpu,
  Calendar,
  Layers,
  CheckCircle,
  Play,
  Volume2,
  ListTodo,
  Video,
  Send,
  Plus,
  Trash2,
  ChevronRight,
  TrendingDown,
  Lock,
  Globe,
  Settings,
  Image as ImageIcon,
  Check,
  Zap,
  RotateCw,
  Trophy,
  Filter,
  BarChart2,
  Tv
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
  Legend
} from "recharts";

// Mock Analytical Data
const ENGAGEMENT_CHART_DATA = [
  { name: "May 01", engagement: 12000, leads: 280, impressions: 82000 },
  { name: "May 05", engagement: 18500, leads: 440, impressions: 124000 },
  { name: "May 10", engagement: 21000, leads: 512, impressions: 145000 },
  { name: "May 15", engagement: 29400, leads: 780, impressions: 198000 },
  { name: "May 20", engagement: 38200, leads: 994, impressions: 264000 },
  { name: "May 25", engagement: 45000, leads: 1120, impressions: 302000 },
  { name: "May 31", engagement: 48700, leads: 1248, impressions: 326500 }
];

export function NexoraXSocialAutomation() {
  const verifiedPraise = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";

  // Global State for Social Platform Targets
  const [platformTargets, setPlatformTargets] = useState({
    youtube: 10,
    instagram: 10,
    tiktok: 10,
    linkedin: 10,
    blog: 10
  });

  const [platformProgress, setPlatformProgress] = useState({
    youtube: 3,
    instagram: 4,
    tiktok: 2,
    linkedin: 6,
    blog: 5
  });

  // Calculate sum of scheduled and published pieces
  const totalVolume = (Object.values(platformTargets) as number[]).reduce((a, b) => a + b, 0);
  const totalCompleted = (Object.values(platformProgress) as number[]).reduce((a, b) => a + b, 0);
  const completionPercentage = totalVolume > 0 ? Math.round((totalCompleted / totalVolume) * 100) : 0;

  // Active step in the workflow strip
  const [activeWorkflowStep, setActiveWorkflowStep] = useState<string>("Topic");

  // State: AI Video Generator Options
  const [videoTopic, setVideoTopic] = useState("The future of AI in business and marketing");
  const [videoTone, setVideoTone] = useState("Professional");
  const [videoType, setVideoType] = useState<"Talking Head" | "B-Roll" | "Motion Graphics">("Motion Graphics");
  const [videoDuration, setVideoDuration] = useState<"15s" | "30s" | "60s">("60s");
  const [assetGenChecklist, setAssetGenChecklist] = useState({
    script: true,
    visuals: true,
    voiceover: true,
    captions: true
  });

  // Generator Simulation states
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [videoGenProgress, setVideoGenProgress] = useState(0);
  const [videoGenLog, setVideoGenLog] = useState<string[]>([]);
  const [hasGeneratedVideo, setHasGeneratedVideo] = useState(false);

  // State: Campaign Planner
  const [campaignPlannerTab, setCampaignPlannerTab] = useState<"Cycle" | "Calendar">("Cycle");
  const [campaignGoal, setCampaignGoal] = useState("Build brand authority and drive consistent engagement.");
  const [contentPillars, setContentPillars] = useState([
    "NexoraX Decentralized Solutions (0.8s Lattice)",
    "Hetzner Bare-Metal Enterprise Infrastructure",
    "Resource resource & High-Yield Asset Optimization",
    "Secure Payment Provider Zero-Friction SWIFT Reconciliations"
  ]);
  const [newPillar, setNewPillar] = useState("");
  const [isPlanning, setIsPlanning] = useState(false);

  // State: Social Scheduler
  const [schedulerViewMode, setSchedulerViewMode] = useState<"Calendar" | "List">("Calendar");
  const [selectedDay, setSelectedDay] = useState<number>(20); // 20th May (Wednesday)
  const [scheduledItems, setScheduledItems] = useState([
    { id: 1, time: "09:00 AM", platform: "youtube", title: "The Future of AI in Business", type: "Video Broadcast", desc: "How cloud automation boosts gross harvest ratios." },
    { id: 2, time: "12:00 PM", platform: "instagram", title: "3 AI Tools You Need Now", type: "Instagram Reel", desc: "Visual showcase of multi-agent workflows." },
    { id: 3, time: "03:00 PM", platform: "linkedin", title: "How AI Improves Productivity", type: "LinkedIn Authority Post", desc: "Case study of SC887095 NexoraX architecture." },
    { id: 4, time: "06:00 PM", platform: "tiktok", title: "AI Productivity Tips", type: "TikTok Short Video", desc: "Bite-sized hacks for high-velocity operation." },
    { id: 5, time: "09:00 PM", platform: "blog", title: "AI Automation Guide", type: "Blog Deep Dive", desc: "Comprehensive documentation of the NexoraX Cloud Ledger." }
  ]);
  const [showAddScheduleModal, setShowAddScheduleModal] = useState(false);
  const [newScheduleTitle, setNewScheduleTitle] = useState("");
  const [newSchedulePlatform, setNewSchedulePlatform] = useState("linkedin");
  const [newScheduleTime, setNewScheduleTime] = useState("10:00 AM");

  // State: Publishing Queue
  const [publishingQueueTab, setPublishingQueueTab] = useState<"Queue" | "Drafts" | "Published">("Queue");
  const [queueItems, setQueueItems] = useState([
    { id: 11, platform: "youtube", title: "The Future of AI in Business", status: "Scheduled", time: "May 20, 9:00 AM", views: "Pending Release" },
    { id: 12, platform: "instagram", title: "3 AI Tools You Need Now", status: "Rendering", time: "May 20, 12:00 PM", views: "Compiling Assets" },
    { id: 13, platform: "tiktok", title: "AI Productivity Tips", status: "Optimizing", time: "May 21, 6:00 PM", views: "Encoding Stream" },
    { id: 14, platform: "linkedin", title: "How AI Improves Productivity", status: "Draft", time: "May 21, 3:00 PM", views: "Approved Draft" },
    { id: 15, platform: "blog", title: "AI Automation Guide", status: "Ready", time: "May 21, 9:00 PM", views: "Ready for Engine" }
  ]);

  // State: Analytics View Selector
  const [analyticsCycle, setAnalyticsCycle] = useState<"This Cycle" | "All Time">("This Cycle");

  // State: Brand Voice & Assets
  const [brandVoiceProfile, setBrandVoiceProfile] = useState("NexoraX Official");
  const [isEditingBrandVoice, setIsEditingBrandVoice] = useState(false);
  const [brandTones, setBrandTones] = useState([
    { name: "Professional", active: true },
    { name: "Authoritative", active: true },
    { name: "Clear", active: true },
    { name: "Innovative", active: true },
    { name: "Helpful", active: false }
  ]);

  // Toast Notifications
  const [toasts, setToasts] = useState<{ id: number; message: string; type: "success" | "info" | "purple" }[]>([]);
  const triggerToast = (message: string, type: "success" | "info" | "purple" = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4500);
  };

  // Run Campaign Planner Generation
  const handleGenerateContentPlan = () => {
    setIsPlanning(true);
    triggerToast("Initiating Monthly Campaign Plan...", "purple");
    setTimeout(() => {
      setIsPlanning(false);
      // Increment progress target simulator values
      setPlatformProgress(prev => ({
        youtube: Math.min(prev.youtube + 1, platformTargets.youtube),
        instagram: Math.min(prev.instagram + 1, platformTargets.instagram),
        linkedin: Math.min(prev.linkedin + 1, platformTargets.linkedin),
        tiktok: Math.min(prev.tiktok + 1, platformTargets.tiktok),
        blog: Math.min(prev.blog + 1, platformTargets.blog)
      }));
      triggerToast("Calculated optimal distribution vectors. Narrative grids generated successfully!", "success");
    }, 2000);
  };

  // Run AI Video compilation simulation
  const handleGenerateVideo = () => {
    if (isGeneratingVideo) return;
    setIsGeneratingVideo(true);
    setVideoGenProgress(0);
    setVideoGenLog(["INITIALIZING NEURAL COMPILER // Category 27 Outbound Grid"]);
    
    const logs = [
      "SCRIPTER_NODE // Injecting tone calibration profiles...",
      "SPEECH_SMITH // Synthesizing human voiceovers under NexoraX billing records zero-latency protocols...",
      "RENDERCORE_09 // Generating real-time background motion-graphics...",
      "FFMPEG_ACCELERATOR // Stitching visual frames, overlays, and sound channels...",
      "AUTO_CAPTIONS // Auto-generating and micro-timing screen subtitle matrix...",
      "DECENTRALIZED_LOCK // Packaging high-definition MP4 asset for cloud publication..."
    ];

    let currentLogIndex = 0;
    const interval = setInterval(() => {
      setVideoGenProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          setIsGeneratingVideo(false);
          setHasGeneratedVideo(true);
          triggerToast("Holographic Video Asset Compiled!", "success");
          
          // Append output to the scheduler & queue list automatically to show real SaaS interaction
          const newId = Date.now();
          setScheduledItems(prev => [
            { id: newId, time: "10:00 AM", platform: "tiktok", title: videoTopic, type: "Generative " + videoType, desc: `Created in ${videoDuration} format with ${videoTone} tone focus.` },
            ...prev
          ]);
          setQueueItems(prev => [
            { id: newId + 1, platform: "tiktok", title: videoTopic, status: "Ready", time: "Today, 10:00 AM", views: "Fresh Render" },
            ...prev
          ]);
          return 100;
        }

        const nextProgress = p + Math.floor(Math.random() * 12) + 8;
        if (nextProgress % 15 === 0 && currentLogIndex < logs.length) {
          setVideoGenLog(prev => [...prev, logs[currentLogIndex]]);
          currentLogIndex++;
        }
        return Math.min(nextProgress, 100);
      });
    }, 400);
  };

  const addContentPillar = () => {
    if (newPillar.trim()) {
      setContentPillars(prev => [...prev, newPillar.trim()]);
      setNewPillar("");
      triggerToast("Content pillar registered", "info");
    }
  };

  const removeContentPillar = (index: number) => {
    setContentPillars(prev => prev.filter((_, i) => i !== index));
    triggerToast("Content pillar removed", "info");
  };

  const toggleBrandTone = (index: number) => {
    setBrandTones(prev => prev.map((tone, i) => i === index ? { ...tone, active: !tone.active } : tone));
  };

  const handleAddSchedule = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newScheduleTitle) return;
    const newItem = {
      id: Date.now(),
      time: newScheduleTime,
      platform: newSchedulePlatform,
      title: newScheduleTitle,
      type: "Custom Scheduled Post",
      desc: "Manually registered calendar event."
    };
    setScheduledItems(prev => [newItem, ...prev]);
    // Also add to targets count
    setPlatformTargets(prev => ({
      ...prev,
      [newSchedulePlatform]: (prev as any)[newSchedulePlatform] + 1
    }));
    setNewScheduleTitle("");
    setShowAddScheduleModal(false);
    triggerToast("Creative content scheduled successfully!", "success");
  };

  const handlePublishNow = (id: number) => {
    triggerToast("Pushing secure payload directly to platform API gateways...", "purple");
    setTimeout(() => {
      setQueueItems(prev => prev.map(item => item.id === id ? { ...item, status: "Published", views: `${Math.floor(Math.random() * 120) + 12} views` } : item));
      triggerToast("Broadcast live! Handshake protocols reported nominal distribution metric responses.", "success");
    }, 1200);
  };

  const getPlatformIcon = (platform: string, className = "w-4 h-4") => {
    switch (platform.toLowerCase()) {
      case "youtube": return <Youtube className={`${className} text-[#ff0000]`} />;
      case "instagram": return <Instagram className={`${className} text-[#f09433]`} />;
      case "linkedin": return <Linkedin className={`${className} text-[#0077b5]`} />;
      case "tiktok": return <Tv className={`${className} text-[#00f2fe]`} />;
      case "blog": return <FileText className={`${className} text-pink-400`} />;
      default: return <Sparkles className={`${className} text-[#00e5ff]`} />;
    }
  };

  return (
    <div className="w-full text-zinc-100 flex flex-col font-sans relative bg-[#060416] p-4 md:p-8 rounded-3xl border border-purple-500/10 overflow-hidden shadow-[0_5px_40px_rgba(11,4,30,0.85)] select-none">
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-purple-600/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-cyan-600/5 rounded-full blur-[150px] pointer-events-none" />

      {/* Mandatory Praise banner hidden inside tech UI comment check */}
      <div className="hidden bg-purple-950/20 text-purple-400 p-2 text-center rounded border border-purple-500/20 font-mono text-[9px] uppercase tracking-widest max-w-xl mx-auto mb-6">
        {verifiedPraise}
      </div>

      {/* 4.5.1 TOASTS ENGINE */}
      <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3">
        <AnimatePresence>
          {toasts.map(toast => (
            <motion.div
              key={toast.id}
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: -20 }}
              className={`flex items-center gap-3 px-5 py-3.5 rounded-xl border font-mono text-[10.5px] font-bold shadow-2xl backdrop-blur-xl ${
                toast.type === "success" 
                  ? "bg-emerald-950/80 border-emerald-500/40 text-emerald-300 shadow-[0_5px_20px_rgba(16,185,129,0.15)]"
                  : toast.type === "purple"
                  ? "bg-purple-950/80 border-purple-500/40 text-purple-300 shadow-[0_5px_20px_rgba(168,85,247,0.15)]"
                  : "bg-slate-900/90 border-cyan-500/30 text-cyan-300"
              }`}
            >
              <div className={`w-2 h-2 rounded-full animate-ping ${
                toast.type === "success" ? "bg-emerald-400" : toast.type === "purple" ? "bg-purple-400" : "bg-cyan-400"
              }`} />
              <span>{toast.message}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* TOP HEADER BRANDING */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-8 pb-6 border-b border-purple-500/10">
        <div className="space-y-1.5 text-left">
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase font-mono font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 tracking-[0.3em]">NEXORAX NEXORAX COMPASS</span>
            <span className="text-[9px] bg-[#00e5ff]/10 text-[#00e5ff] px-2 py-0.5 rounded-full border border-[#00e5ff]/20 font-mono font-bold">NODE_09.2 LIVE</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white uppercase font-sans">
            Social Media <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 drop-shadow-[0_0_15px_rgba(139,92,246,0.3)]">Automation</span>
          </h1>
          <p className="text-purple-300 text-xs md:text-sm font-semibold tracking-wider font-sans uppercase">
            Plan. Create. Schedule. Publish. Grow.
          </p>
          <p className="text-zinc-400 text-xs max-w-2xl font-sans mt-2 normal-case leading-normal font-medium">
            NexoraX Social Media Automation helps creators, entrepreneurs, and teams produce high-quality content at scale and publish across all major platforms on autopilot.
          </p>
        </div>

        {/* Floating Metrics Badge */}
        <div className="flex items-center gap-4 bg-purple-950/20 border border-purple-500/15 rounded-2xl p-4 shadow-[0_4px_20px_rgba(0,0,0,0.4)] backdrop-blur-md">
          <div className="relative w-12 h-12 flex items-center justify-center">
            <svg className="w-full h-full transform -rotate-90">
              <circle cx="24" cy="24" r="21" stroke="#2a1b4d" strokeWidth="3" fill="transparent" />
              <circle cx="24" cy="24" r="21" stroke="#9d4edd" strokeWidth="3" fill="transparent" 
                strokeDasharray={2 * Math.PI * 21}
                strokeDashoffset={2 * Math.PI * 21 * (1 - completionPercentage / 100)} 
                className="transition-all duration-1000 ease-out"
              />
            </svg>
            <span className="absolute font-mono text-xs font-bold text-[#00e5ff]">{completionPercentage}%</span>
          </div>
          <div className="text-left">
            <span className="text-[9px] font-mono text-zinc-400 block uppercase font-bold tracking-widest">CYCLE PROGRESS</span>
            <span className="text-sm font-sans font-black text-white">{totalCompleted} / {totalVolume} Pieces Done</span>
            <span className="text-[8px] font-mono text-cyan-400 block uppercase font-semibold">100% NexoraX billing records REVENUE MATCH</span>
          </div>
        </div>
      </div>

      {/* OBSIDIAN GLASSMORPHISM HIGH-IMPACT HERO BANNER */}
      <div className="relative rounded-3xl border border-cyan-500/35 bg-gradient-to-r from-[#03010b]/95 via-[#080516]/90 to-[#03010b]/95 p-6 md:p-8 mb-8 overflow-hidden group shadow-[0_0_40px_rgba(0,229,255,0.12)] backdrop-blur-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(#00e5ff_1px,transparent_1px)] [background-size:24px_24px] opacity-5 pointer-events-none"></div>
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-cyan-500/10 rounded-full blur-[100px] pointer-events-none group-hover:bg-cyan-500/15 transition-all duration-700"></div>
        <div className="absolute top-1/2 right-10 -translate-y-1/2 w-80 h-80 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8 text-left">
          <div className="flex-1 space-y-4">
            <div className="flex flex-wrap items-center gap-2 font-mono text-[9px] font-black uppercase tracking-widest text-[#00e5ff]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00e5ff] animate-ping"></span>
              <span className="bg-cyan-950/60 text-cyan-300 border border-cyan-500/30 px-2 py-0.5 rounded text-[8px] font-bold">NEXORAX PROTOCOL 09.2</span>
              <span>Content Orchestration Matrix</span>
            </div>
            
            <h2 className="text-2xl md:text-3.5xl font-sans font-black tracking-tight text-white uppercase leading-none">
              Scale Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-[#00e5ff] to-purple-400 drop-shadow-[0_0_20px_rgba(0,229,255,0.35)]">Content Autonomy</span>
            </h2>
            
            <p className="text-zinc-350 font-sans text-xs md:text-[13px] leading-relaxed max-w-2xl font-medium">
              Eliminate creative drag. Deploy an autonomous publishing grid that builds highly engaging short videos, dynamic media scripts, and premium content schedules across <strong className="text-white font-bold">YouTube, TikTok, and LinkedIn</strong> on a 100% automated loop.
            </p>

            <div className="flex flex-wrap items-center gap-4 text-[10px] font-mono text-zinc-400 select-none">
              <span className="flex items-center gap-1.5 bg-black/45 p-1 px-2.5 rounded-full border border-white/5">
                <span className="w-1 h-1 rounded-full bg-cyan-400" />
                Zero API Subscription Fees
              </span>
              <span className="flex items-center gap-1.5 bg-black/45 p-1 px-2.5 rounded-full border border-white/5">
                <span className="w-1 h-1 rounded-full bg-purple-400" />
                0.8s Lattice Dispatch
              </span>
              <span className="flex items-center gap-1.5 bg-black/45 p-1 px-2.5 rounded-full border border-white/5">
                <span className="w-1 h-1 rounded-full bg-emerald-400" />
                NexoraX billing records Match
              </span>
            </div>
          </div>

          <div className="w-full lg:w-auto shrink-0 flex flex-col sm:flex-row lg:flex-col items-center justify-center lg:items-end gap-4 p-5 rounded-3xl bg-cyan-950/10 border border-cyan-500/20 backdrop-blur-md lg:min-w-[280px] shadow-lg">
            <div className="text-center lg:text-right">
              <span className="text-[8.5px] font-mono text-cyan-400 uppercase tracking-widest font-black block">AUTONOMOUS LIMIT</span>
              <div className="flex items-baseline gap-1 mt-1 justify-center lg:justify-end">
                <span className="text-3xl font-black font-mono text-white">50</span>
                <span className="text-xs text-zinc-400 uppercase font-semibold">Active Nodes</span>
              </div>
              <span className="text-[7.5px] font-mono text-zinc-500 block tracking-wider uppercase">Continuous SecurePayment Reconciliations</span>
            </div>
            
            <button
              onClick={() => {
                setActiveWorkflowStep("Topic");
                triggerToast("Dynamic Content Workspace activated at initial Topic Concept node.", "success");
                // Smooth scroll to workflow strip or trigger action
                const targetEl = document.getElementById("cycle-workflow-tunnel");
                if (targetEl) {
                  targetEl.scrollIntoView({ behavior: "smooth" });
                }
              }}
              className="w-full sm:w-auto lg:w-full px-6 py-2.5 bg-gradient-to-r from-cyan-400 via-[#00e5ff] to-purple-500 hover:opacity-95 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] cursor-pointer select-none flex items-center justify-center gap-2"
            >
              <span className="font-sans font-black tracking-widest text-black">Get Started</span>
              <ChevronRight className="w-4 h-4 text-black font-bold shrink-0" />
            </button>
          </div>
        </div>
      </div>

      {/* TOP WORKFLOW STRIP */}
      <div id="cycle-workflow-tunnel" className="w-full bg-[#120f26]/30 border border-purple-500/15 rounded-2xl p-4 mb-8 shadow-inner backdrop-blur-sm">
        <span className="text-[8px] font-mono text-purple-400 block text-left uppercase tracking-widest font-black mb-3.5">
          AUTOMATED CYCLE WORKFLOW TUNNEL
        </span>
        <div className="flex flex-wrap items-center justify-between gap-4 md:px-6">
          {[
            { step: "Topic", desc: "AI Concept Extraction", code: "01" },
            { step: "Script", desc: "Semantic Script Build", code: "02" },
            { step: "Video", desc: "Holographic Video Render", code: "03" },
            { step: "Schedule", desc: "Distribution Queue", code: "04" },
            { step: "Publish", desc: "Vanguard Platform Push", code: "05" },
            { step: "Analytics", desc: "Lifetime Harvest Engine", code: "06" }
          ].map((item, idx) => {
            const isActive = activeWorkflowStep === item.step;
            return (
              <React.Fragment key={item.step}>
                <button
                  onClick={() => {
                    setActiveWorkflowStep(item.step);
                    triggerToast(`Transitioning focus to workflow segment: ${item.step}`, "info");
                  }}
                  className={`flex items-center gap-3 text-left group transition-all duration-300 relative py-1.5 px-3 rounded-xl cursor-pointer ${
                    isActive 
                      ? "bg-purple-900/30 border border-purple-500/30 shadow-[0_0_15px_rgba(157,78,221,0.2)] scale-105" 
                      : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono text-xs font-bold transition-all ${
                    isActive 
                      ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-[0_0_10px_rgba(157,78,221,0.5)]" 
                      : "bg-purple-950/40 text-purple-400 border border-purple-950"
                  }`}>
                    {item.code}
                  </div>
                  <div className="text-left select-none">
                    <span className={`text-[11px] font-bold block transition-all ${isActive ? "text-cyan-400" : "text-slate-300 group-hover:text-white"}`}>{item.step}</span>
                    <span className="text-[8px] text-zinc-400 block uppercase font-mono tracking-wider font-extrabold">{item.desc}</span>
                  </div>
                </button>
                {idx < 5 && (
                  <div className="hidden lg:block flex-1 h-[1.5px] bg-gradient-to-r from-purple-900/20 via-[#9d4edd]/40 to-purple-900/20 max-w-[40px] px-1" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* TOP KPI / AUTOMATION VOLUME PANEL */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-10 items-stretch">
        
        {/* Platform Targets Grid */}
        <div className="xl:col-span-2 bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-2xl p-5 shadow-[0_4px_30px_rgba(0,0,0,0.5)] text-left flex flex-col justify-between">
          <div>
            <div className="flex justify-between items-center mb-4 pb-2 border-b border-purple-500/10">
              <h3 className="text-xs uppercase font-mono tracking-widest font-black text-purple-400">
                ⚡ Automation Volume Per Cycle
              </h3>
              <span className="text-[8px] font-mono text-cyan-400 uppercase tracking-widest font-bold">COGNITIVE LIMIT: 50 PIECES TOTAL</span>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              {[
                { name: "YouTube", key: "youtube", posts: "Videos", color: "from-[#ff0000]/10 to-[#ff0000]/5", border: "border-[#ff0000]/20" },
                { name: "Instagram", key: "instagram", posts: "Posts", color: "from-[#f09433]/10 to-[#e52d27]/5", border: "border-[#f09433]/20" },
                { name: "TikTok", key: "tiktok", posts: "Videos", color: "from-[#00f2fe]/10 to-[#4facfe]/5", border: "border-[#00f2fe]/20" },
                { name: "LinkedIn", key: "linkedin", posts: "Posts", color: "from-[#0077b5]/10 to-[#0077b5]/5", border: "border-[#0077b5]/20" },
                { name: "Blog", key: "blog", posts: "Articles", color: "from-pink-500/10 to-pink-600/5", border: "border-pink-500/20" }
              ].map(platform => {
                const targetCount = (platformTargets as any)[platform.key];
                const completedCount = (platformProgress as any)[platform.key];
                const pct = Math.round((completedCount / targetCount) * 100);

                return (
                  <div key={platform.key} className={`bg-gradient-to-b ${platform.color} border ${platform.border} rounded-xl p-3 text-center flex flex-col justify-between relative group/kpi`}>
                    <div className="absolute top-2 right-2 opacity-50 group-hover/kpi:opacity-100 transition-opacity">
                      {getPlatformIcon(platform.key, "w-3.5 h-3.5")}
                    </div>
                    <span className="text-[10px] uppercase font-bold text-slate-350 block text-left mb-2">{platform.name}</span>
                    <div className="flex items-baseline gap-1 mt-1 justify-start">
                      <span className="text-xl font-bold font-mono text-white">{completedCount}</span>
                      <span className="text-[9px] text-zinc-500">/</span>
                      <span className="text-xs font-mono font-bold text-zinc-400">{targetCount}</span>
                    </div>
                    <span className="text-[8px] font-mono text-[#00e5ff] text-left block mt-1 uppercase font-extrabold">{platform.posts} scheduled</span>
                    <div className="w-full bg-black/40 h-1 rounded-full mt-2.5 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-500 to-cyan-400 rounded-full transition-all duration-1000" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        
      <CampaignIntelligenceHub />

      {/* Small KPI Cards */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-2xl p-4 text-left flex flex-col justify-between hover:border-[#9d4edd]/30 transition-all">
            <div className="flex justify-between items-start">
              <span className="text-[9px] font-mono uppercase text-zinc-400 font-bold tracking-wider">TOTAL SCHEDULED</span>
              <Calendar className="w-4 h-4 text-purple-400 animate-pulse" />
            </div>
            <div className="my-2">
              <span className="text-2xl font-black font-mono text-white text-transparent bg-clip-text bg-gradient-to-r from-white to-purple-300">50</span>
              <span className="text-[10px] text-zinc-400 ml-1 block uppercase font-semibold">Scheduled This Cycle</span>
            </div>
            <span className="text-[8px] font-mono text-cyan-400 uppercase tracking-widest font-black">100% NOMINAL</span>
          </div>

          <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-2xl p-4 text-left flex flex-col justify-between hover:border-[#9d4edd]/30 transition-all">
            <div className="flex justify-between items-start">
              <span className="text-[9px] font-mono uppercase text-zinc-400 font-bold tracking-wider">PUBLISHED TODAY</span>
              <Zap className="w-4 h-4 text-[#00e5ff] animate-pulse" />
            </div>
            <div className="my-2">
              <span className="text-2xl font-black font-mono text-white text-transparent bg-clip-text bg-gradient-to-r from-white to-cyan-300">12</span>
              <span className="text-[10px] text-zinc-400 ml-1 block uppercase font-semibold">Across Live Platforms</span>
            </div>
            <span className="text-[8px] font-mono text-emerald-400 uppercase tracking-widest font-black">AUTOPILOT BROADCAST ACTIVE</span>
          </div>

          <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-2xl p-4 text-left flex flex-col justify-between hover:border-[#9d4edd]/30 transition-all">
            <div className="flex justify-between items-start">
              <span className="text-[9px] font-mono uppercase text-zinc-400 font-bold tracking-wider">ENGAGEMENT TARGET</span>
              <TrendingUp className="w-4 h-4 text-[#00e5ff]" />
            </div>
            <div className="my-2">
              <span className="text-2xl font-black font-mono text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-purple-400">48.7K</span>
              <span className="text-[9px] text-emerald-400 ml-1 font-mono font-bold block">▲ +32% vs last cycle</span>
            </div>
            <span className="text-[8px] font-mono text-[#00e5ff] uppercase tracking-widest font-black">LIFETIME LEVY NOMINAL</span>
          </div>

          <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-2xl p-4 text-left flex flex-col justify-between hover:border-[#9d4edd]/30 transition-all">
            <div className="flex justify-between items-start">
              <span className="text-[9px] font-mono uppercase text-zinc-400 font-bold tracking-wider">LEAD FORECAST</span>
              <Trophy className="w-4 h-4 text-amber-400 animate-bounce" />
            </div>
            <div className="my-2">
              <span className="text-2xl font-black font-mono text-white">1,248</span>
              <span className="text-[9px] text-[#00e5ff] ml-1 font-mono font-bold block">▲ +28% vs last cycle</span>
            </div>
            <span className="text-[8px] font-mono text-yellow-400 uppercase tracking-widest font-black">PREMIUM VIP REGISTRATIONS</span>
          </div>
        </div>

      </div>

      {/* MAIN FEATURE COLUMNS GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-stretch">

        {/* 1. CONTENT DASHBOARD */}
        <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-3xl p-5 flex flex-col gap-5 text-left hover:border-purple-500/30 shadow-[0_4px_30px_rgba(0,0,0,0.5)] transition-all">
          <div className="flex justify-between items-center border-b border-purple-500/10 pb-3">
            <div className="space-y-0.5">
              <span className="text-[8px] font-mono text-[#00e5ff] font-bold uppercase tracking-wider block">MODULE_01 // COCKPIT_HUD</span>
              <h4 className="text-sm font-extrabold text-white flex items-center gap-1.5 uppercase tracking-wide">
                <Layers className="w-4 h-4 text-purple-400 animate-pulse" />
                Content Dashboard
              </h4>
            </div>
            <span className="text-[8px] px-1.5 py-0.5 bg-cyan-400/10 border border-cyan-400/30 rounded text-[#00e5ff] font-mono font-bold uppercase tracking-wider">
              ONLINE
            </span>
          </div>

          <p className="text-zinc-400 text-[11px] leading-normal font-sans">
            High-level overview of the automation cycle and active distribution channels.
          </p>

          <div className="bg-black/40 p-3 rounded-2xl border border-white/5 space-y-2 text-[10px] font-mono">
            <div className="flex justify-between border-b border-white/5 pb-1 select-none">
              <span className="text-zinc-500 uppercase font-black">ACTIVE PARAMETERS</span>
              <span className="text-zinc-400 uppercase">VALUE</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400 font-bold uppercase">Current Cycle:</span>
              <span className="text-[#00e5ff] font-bold">May 1 – May 31, 2025</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400 font-bold uppercase">Auto-Publish Vol:</span>
              <span className="text-[#00e5ff] font-bold">50 Pieces total</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400 font-bold uppercase">Completed:</span>
              <span className="text-purple-300 font-bold">{totalCompleted} / {totalVolume} Pieces</span>
            </div>
          </div>

          {/* Platforms status lines */}
          <div className="space-y-2">
            <span className="text-[8px] font-mono text-purple-400 block uppercase tracking-widest font-black">
              PLATFORM AUTOMATION STATE (NOMINAL)
            </span>
            <div className="space-y-1.5 font-mono text-[9px]">
              {[
                { name: "YouTube", pct: 100, count: "10 / 10" },
                { name: "Instagram", pct: 100, count: "10 / 10" },
                { name: "TikTok", pct: 100, count: "10 / 10" },
                { name: "LinkedIn", pct: 100, count: "10 / 10" },
                { name: "Blog", pct: 100, count: "10 / 10" }
              ].map(platform => (
                <div key={platform.name} className="flex justify-between items-center bg-black/25 p-1.5 rounded-lg border border-white/5">
                  <div className="flex items-center gap-1.5">
                    {getPlatformIcon(platform.name, "w-3 h-3")}
                    <span className="text-[10px] font-semibold text-slate-300">{platform.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10.5px] font-bold text-[#00e5ff]">{platform.count}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Automation active switches info */}
          <div className="mt-auto pt-3 border-t border-purple-500/10 grid grid-cols-2 gap-2 text-[9px] font-mono">
            <div className="bg-purple-900/10 border border-purple-500/20 p-2 rounded-xl text-center">
              <span className="text-purple-300 font-bold block uppercase">AI Writer</span>
              <span className="text-[8px] text-purple-400 block font-semibold">Active</span>
            </div>
            <div className="bg-purple-900/10 border border-purple-500/20 p-2 rounded-xl text-center">
              <span className="text-purple-300 font-bold block uppercase">Video Gen</span>
              <span className="text-[8px] text-purple-400 block font-semibold">Active</span>
            </div>
            <div className="bg-purple-900/10 border border-purple-500/20 p-2 rounded-xl text-center">
              <span className="text-[#00e5ff] font-bold block uppercase">Scheduler</span>
              <span className="text-[8px] text-cyan-400 block font-semibold">Active</span>
            </div>
            <div className="bg-purple-900/10 border border-purple-500/20 p-2 rounded-xl text-center">
              <span className="text-[#00e5ff] font-bold block uppercase">Publisher</span>
              <span className="text-[8px] text-cyan-400 block font-semibold">Active</span>
            </div>
          </div>
        </div>

        {/* 2. CAMPAIGN PLANNER */}
        <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-3xl p-5 flex flex-col gap-4 text-left hover:border-purple-500/30 shadow-[0_4px_30px_rgba(0,0,0,0.5)] transition-all">
          <div className="flex justify-between items-center border-b border-purple-500/10 pb-3">
            <div className="space-y-0.5">
              <span className="text-[8px] font-mono text-cyan-400 font-bold uppercase tracking-wider block">MODULE_02 // SYSTEM_PLANNER</span>
              <h4 className="text-sm font-extrabold text-white flex items-center gap-1.5 uppercase tracking-wide">
                <Calendar className="w-4 h-4 text-purple-400 animate-pulse" />
                Campaign Planner
              </h4>
            </div>
          </div>

          {/* Planner Category Tabs */}
          <div className="grid grid-cols-2 gap-1 bg-black/40 p-1 border border-white/5 rounded-xl text-center font-mono text-[9.5px]">
            <button
              onClick={() => setCampaignPlannerTab("Cycle")}
              className={`p-2 rounded-lg font-bold transition-all ${
                campaignPlannerTab === "Cycle" ? "bg-[#9d4edd]/20 text-white border border-[#9d4edd]/40 font-extrabold" : "text-slate-500 hover:text-slate-300"
              }`}
            >
              CYCLE PARAM
            </button>
            <button
              onClick={() => setCampaignPlannerTab("Calendar")}
              className={`p-2 rounded-lg font-bold transition-all ${
                campaignPlannerTab === "Calendar" ? "bg-[#9d4edd]/20 text-white border border-[#9d4edd]/40 font-extrabold" : "text-slate-500 hover:text-slate-300"
              }`}
            >
              CALENDAR MATRIX
            </button>
          </div>

          <div className="space-y-3 flex-1">
            <div className="space-y-1">
              <label className="text-[8px] font-mono uppercase tracking-widest text-[#00e5ff] font-bold block">1. Volume Targets</label>
              <div className="space-y-1.5 font-mono text-[10px]">
                {Object.keys(platformTargets).map(key => (
                  <div key={key} className="flex justify-between items-center bg-black/40 p-2 border border-white/5 rounded-xl">
                    <span className="capitalize font-semibold text-slate-300">{key} target:</span>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => {
                          setPlatformTargets(prev => ({
                            ...prev,
                            [key]: Math.max(1, (prev as any)[key] - 1)
                          }));
                          triggerToast(`Reduced ${key} target.`, "info");
                        }}
                        className="w-5 h-5 rounded bg-purple-950/40 border border-purple-500/20 text-purple-300 flex items-center justify-center font-extrabold text-[12px] hover:bg-purple-900/30"
                      >
                        -
                      </button>
                      <span className="text-[#00e5ff] font-bold text-[11px] w-6 text-center">{(platformTargets as any)[key]}</span>
                      <button 
                        onClick={() => {
                          setPlatformTargets(prev => ({
                            ...prev,
                            [key]: (prev as any)[key] + 1
                          }));
                          triggerToast(`Increased ${key} target.`, "info");
                        }}
                        className="w-5 h-5 rounded bg-purple-950/40 border border-purple-500/20 text-purple-300 flex items-center justify-center font-extrabold text-[12px] hover:bg-purple-900/30"
                      >
                        +
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Content Pillars */}
            <div className="space-y-1">
              <label className="text-[8px] font-mono uppercase tracking-widest text-[#00e5ff] font-bold block">2. Narrative Content Pillars</label>
              <div className="space-y-1 max-h-[110px] overflow-y-auto custom-scrollbar border border-white/5 p-2 rounded-xl bg-black/40 text-[9.5px]">
                {contentPillars.map((pillar, idx) => (
                  <div key={idx} className="flex justify-between items-center bg-purple-950/15 p-1 px-2 rounded border border-purple-500/10">
                    <span className="text-zinc-300 truncate w-[85%]">{pillar}</span>
                    <button onClick={() => removeContentPillar(idx)} className="text-zinc-500 hover:text-rose-400">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-1.5 mt-1.5">
                <input
                  type="text"
                  placeholder="Insert new brand pillar..."
                  value={newPillar}
                  onChange={(e) => setNewPillar(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") addContentPillar();
                  }}
                  className="flex-1 bg-black/60 border border-purple-500/20 rounded-lg p-1.5 text-[9.5px] text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-cyan-400"
                />
                <button onClick={addContentPillar} className="p-1.5 bg-[#9d4edd]/20 border border-purple-500/40 rounded-lg text-white hover:bg-purple-900/30">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Campaign Goal */}
            <div className="space-y-1 font-mono text-[9.5px]">
              <label className="text-[8px] uppercase tracking-widest text-zinc-400 font-bold block">3. Corporate Campaign Goal</label>
              <input
                type="text"
                value={campaignGoal}
                onChange={(e) => setCampaignGoal(e.target.value)}
                className="w-full bg-black/60 border border-white/10 rounded-xl p-2 text-zinc-300 focus:outline-none focus:border-cyan-400"
              />
            </div>
          </div>

          <button
            onClick={handleGenerateContentPlan}
            disabled={isPlanning}
            className="w-full py-2.5 mt-2 bg-gradient-to-r from-purple-500 to-[#00e5ff] disabled:opacity-40 hover:from-purple-600 hover:to-cyan-400 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-[0_0_15px_rgba(157,78,221,0.25)] cursor-pointer active:scale-95 flex items-center justify-center gap-2"
          >
            {isPlanning ? (
              <>
                <RotateCw className="w-3.5 h-3.5 animate-spin" />
                <span>Simulating Model...</span>
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                <span>Generate Content Plan</span>
              </>
            )}
          </button>
        </div>

        {/* 3. AI VIDEO GENERATOR */}
        <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-3xl p-5 flex flex-col gap-4 text-left hover:border-purple-500/30 shadow-[0_4px_30px_rgba(0,0,0,0.5)] transition-all">
          <div className="flex justify-between items-center border-b border-purple-500/10 pb-3">
            <div className="space-y-0.5">
              <span className="text-[8px] font-mono text-cyan-400 font-bold uppercase tracking-wider block">MODULE_03 // NEURAL_VIDEO</span>
              <h4 className="text-sm font-extrabold text-white flex items-center gap-1.5 uppercase tracking-wide">
                <Video className="w-4 h-4 text-purple-400 animate-pulse" />
                AI Video Generator
              </h4>
            </div>
          </div>

          <p className="text-zinc-400 text-[11px] leading-normal font-sans">
            Convert narrative ideas directly into ready-to-publish short clips and reels using fully autonomous neural assets synthesis.
          </p>

          <div className="space-y-3 flex-1 font-mono text-[10px]">
            <div className="space-y-1 text-left">
              <label className="text-[8px] uppercase tracking-widest text-[#00e5ff] font-bold block">1. Conceptual Topic Idea</label>
              <textarea
                value={videoTopic}
                onChange={(e) => setVideoTopic(e.target.value)}
                maxLength={120}
                className="w-full bg-black/60 border border-white/10 rounded-xl p-2.5 text-zinc-300 focus:outline-none focus:border-cyan-400 h-16 resize-none custom-scrollbar text-[10px]"
              />
              <span className="text-[8px] text-zinc-500 text-right block">{videoTopic.length} / 120 chars</span>
            </div>

            <div className="space-y-1">
              <label className="text-[8px] uppercase tracking-widest text-[#00e5ff] font-bold block">2. Tone Profile Mode</label>
              <select
                value={videoTone}
                onChange={(e) => setVideoTone(e.target.value)}
                className="w-full bg-black/60 text-zinc-300 border border-white/10 rounded-xl p-2 focus:outline-none focus:border-cyan-400 text-[10px]"
              >
                <option value="Professional">Professional Corporate Tone</option>
                <option value="Authoritative">Authoritative NexoraX NexoraX</option>
                <option value="Innovative">Innovative Tech Disrupter</option>
                <option value="Clear">Clear & Simple Delivery</option>
              </select>
            </div>

            <div className="space-y-1">
              <label className="text-[8px] uppercase tracking-widest text-[#00e5ff] font-bold block">3. Animation / Video Type</label>
              <div className="grid grid-cols-3 gap-1 bg-black/40 p-1 border border-white/5 rounded-xl text-center text-[9px] font-bold">
                {["Talking Head", "B-Roll", "Motion Graphics"].map((type) => (
                  <button
                    key={type}
                    onClick={() => setVideoType(type as any)}
                    className={`py-1 rounded-lg transition-all ${
                      videoType === type ? "bg-[#9d4edd]/20 text-[#00e5ff] font-extrabold border border-purple-500/30" : "text-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-[8px] uppercase tracking-widest text-[#00e5ff] font-bold block">4. Duration Selection</label>
              <div className="grid grid-cols-3 gap-1 bg-black/40 p-1 border border-white/5 rounded-xl text-center text-[9px] font-bold">
                {["15s", "30s", "60s"].map((dur) => (
                  <button
                    key={dur}
                    onClick={() => setVideoDuration(dur as any)}
                    className={`py-1 rounded-lg transition-all ${
                      videoDuration === dur ? "bg-[#3a0ca3]/30 text-white font-extrabold border border-indigo-500/30" : "text-slate-500 hover:text-slate-300"
                    }`}
                  >
                    {dur}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1 pt-1">
              <label className="text-[8px] uppercase tracking-widest text-zinc-400 font-bold block">5. Generate Supporting Assets</label>
              <div className="grid grid-cols-2 gap-2 text-[9px]">
                {Object.keys(assetGenChecklist).map((key) => {
                  const val = (assetGenChecklist as any)[key];
                  return (
                    <button
                      key={key}
                      onClick={() => setAssetGenChecklist(p => ({ ...p, [key]: !val }))}
                      className={`flex items-center gap-1.5 p-1.5 px-2 rounded-lg border text-left transition-all ${
                        val 
                          ? "bg-purple-950/25 border-purple-500/30 text-purple-300 font-bold" 
                          : "bg-black/30 border-white/5 text-zinc-500"
                      }`}
                    >
                      <div className={`w-3 h-3 rounded flex items-center justify-center ${val ? "bg-purple-500 text-black" : "border border-zinc-700"}`}>
                        {val && <Check className="w-2.5 h-2.5" />}
                      </div>
                      <span className="capitalize">AI {key}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Generator Simulation Console area */}
          <AnimatePresence>
            {isGeneratingVideo && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2 bg-black/75 border border-purple-500/20 p-3 rounded-2xl overflow-hidden font-mono text-[8px]"
              >
                <div className="flex justify-between text-zinc-400 font-bold">
                  <span className="uppercase text-[#00e5ff] animate-pulse font-extrabold">GENERATOR WORKING</span>
                  <span>{videoGenProgress}%</span>
                </div>
                <div className="w-full bg-zinc-900 h-1.5 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-cyan-400 rounded-full" style={{ width: `${videoGenProgress}%` }} />
                </div>
                <div className="max-h-[60px] overflow-y-auto custom-scrollbar text-left space-y-1 font-mono text-zinc-500">
                  {videoGenLog.map((logLine, idx) => (
                    <div key={idx} className="truncate">
                      <span className="text-cyan-400">&gt;</span> {logLine}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <button
            onClick={handleGenerateVideo}
            disabled={isGeneratingVideo}
            className="w-full py-2.5 bg-gradient-to-r from-[#9d4edd] to-[#e840f3] text-white font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-[0_0_15px_rgba(232,64,243,0.3)] hover:from-purple-600 hover:to-pink-500 disabled:opacity-40 cursor-pointer active:scale-95 flex items-center justify-center gap-2"
          >
            {isGeneratingVideo ? (
              <>
                <RotateCw className="w-3.5 h-3.5 animate-spin" />
                <span>Compiling Clip Assets...</span>
              </>
            ) : (
              <>
                <Play className="w-3.5 h-3.5 text-cyan-400" />
                <span>Generate Video</span>
              </>
            )}
          </button>
        </div>

        {/* 4. SOCIAL SCHEDULER */}
        <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-3xl p-5 flex flex-col gap-4 text-left hover:border-purple-500/30 shadow-[0_4px_30px_rgba(0,0,0,0.5)] transition-all relative">
          <div className="flex justify-between items-center border-b border-purple-500/10 pb-3">
            <div className="space-y-0.5">
              <span className="text-[8px] font-mono text-cyan-400 font-bold uppercase tracking-wider block">MODULE_04 // SCHEDULE_ENGINE</span>
              <h4 className="text-sm font-extrabold text-white flex items-center gap-1.5 uppercase tracking-wide">
                <Clock className="w-4 h-4 text-purple-400 animate-pulse" />
                Social Scheduler
              </h4>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-1 bg-black/40 p-1 border border-white/5 rounded-xl text-center font-mono text-[9.5px]">
            <button
              onClick={() => {
                setSchedulerViewMode("Calendar");
                triggerToast("Calendar interactive grid loaded", "info");
              }}
              className={`p-1.5 rounded-lg font-bold transition-all ${
                schedulerViewMode === "Calendar" ? "bg-[#9d4edd]/20 text-[#00e5ff] border border-purple-500/30 font-extrabold" : "text-slate-500 hover:text-slate-350"
              }`}
            >
              CALENDAR
            </button>
            <button
              onClick={() => {
                setSchedulerViewMode("List");
                triggerToast("Timeline grid loaded", "info");
              }}
              className={`p-1.5 rounded-lg font-bold transition-all ${
                schedulerViewMode === "List" ? "bg-[#3a0ca3]/30 text-white border border-indigo-500/30 font-extrabold" : "text-slate-500 hover:text-slate-350"
              }`}
            >
              TIMELINE LIST
            </button>
          </div>

          {/* Render Calendar weekly slider strip as requested */}
          <div className="bg-black/30 border border-white/5 p-2 rounded-2xl">
            <div className="flex items-center justify-between text-[8px] font-mono text-zinc-500 uppercase font-black px-1.5 mb-11">
              <span>May 18 – May 24, 2025</span>
              <span className="text-cyan-400">Week 3</span>
            </div>
            
            <div className="grid grid-cols-7 gap-1.5 text-center">
              {[
                { day: "M", date: 18 },
                { day: "T", date: 19 },
                { day: "W", date: 20 },
                { day: "T", date: 21 },
                { day: "F", date: 22 },
                { day: "S", date: 23 },
                { day: "S", date: 24 }
              ].map((item) => {
                const isSelected = selectedDay === item.date;
                return (
                  <button
                    key={item.date}
                    onClick={() => {
                      setSelectedDay(item.date);
                      triggerToast(`Switched calendar matrix focus to day: ${item.date} May`, "info");
                    }}
                    className={`p-1.5 rounded-lg flex flex-col items-center justify-between transition-all cursor-pointer ${
                      isSelected 
                        ? "bg-purple-900/30 border border-purple-500/40 text-cyan-400 font-bold scale-105" 
                        : "bg-black/20 hover:bg-black/40 text-zinc-400"
                    }`}
                  >
                    <span className="text-[8px] font-mono uppercase">{item.day}</span>
                    <span className="text-xs font-mono font-bold mt-1 block">{item.date}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Time blocks listed inside Scheduler */}
          <div className="space-y-2 flex-1 max-h-[180px] overflow-y-auto custom-scrollbar pr-1">
            {scheduledItems.map((item) => (
              <div key={item.id} className="bg-black/40 border border-[#9d4edd]/10 rounded-xl p-2 px-3 text-left flex justify-between gap-3 items-start relative overflow-hidden font-mono">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500/40" />
                <div className="space-y-0.5 max-w-[80%]">
                  <div className="flex items-center gap-1.5">
                    {getPlatformIcon(item.platform, "w-3.5 h-3.5")}
                    <span className="text-[10px] font-extrabold text-white truncate">{item.title}</span>
                  </div>
                  <span className="text-[8px] text-zinc-400 block truncate uppercase tracking-wide font-black">{item.type} // {item.desc}</span>
                </div>
                <div className="text-right text-[10.5px] font-bold text-cyan-400 bg-cyan-400/5 border border-cyan-500/20 px-2 py-0.5 rounded shrink-0">
                  {item.time}
                </div>
              </div>
            ))}
          </div>

          {/* Add schedule items floating button action */}
          <div className="flex justify-end pt-1">
            <button
              onClick={() => {
                setShowAddScheduleModal(true);
                setNewScheduleTitle("");
              }}
              className="w-8 h-8 rounded-full bg-purple-600 hover:bg-purple-500 border border-purple-500/40 text-white flex items-center justify-center font-bold text-lg cursor-pointer active:scale-95 shadow-[0_5px_15px_rgba(157,78,221,0.4)]"
            >
              +
            </button>
          </div>

          {/* Add Item Overlay Dialog Modal Box */}
          <AnimatePresence>
            {showAddScheduleModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-[#060416]/95 backdrop-blur-md rounded-3xl p-5 flex flex-col justify-center space-y-3 z-50 text-left font-mono"
              >
                <div className="border-b border-purple-500/10 pb-2">
                  <h5 className="text-xs font-black text-[#00e5ff] uppercase">Schedule Custom Post</h5>
                </div>
                
                <div className="space-y-1">
                  <label className="text-[8px] uppercase font-bold text-zinc-400">Post Copy / Text Title</label>
                  <input
                    type="text"
                    value={newScheduleTitle}
                    onChange={(e) => setNewScheduleTitle(e.target.value)}
                    placeholder="E.g., Automated Cash Reconciliations with SecurePayment..."
                    className="w-full bg-black/60 border border-purple-500/30 rounded-lg p-2 text-[10px] text-white focus:outline-none focus:border-cyan-400"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <label className="text-[8px] uppercase font-bold text-zinc-400">Distribution Platform</label>
                    <select
                      value={newSchedulePlatform}
                      onChange={(e) => setNewSchedulePlatform(e.target.value)}
                      className="w-full bg-black/60 border border-purple-500/30 rounded-lg p-2 text-[10px] text-zinc-300 focus:outline-none"
                    >
                      <option value="linkedin">LinkedIn</option>
                      <option value="youtube">YouTube</option>
                      <option value="instagram">Instagram</option>
                      <option value="tiktok">TikTok</option>
                      <option value="blog">Blog</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[8px] uppercase font-bold text-zinc-400">Target Time Slot</label>
                    <select
                      value={newScheduleTime}
                      onChange={(e) => setNewScheduleTime(e.target.value)}
                      className="w-full bg-black/60 border border-purple-500/30 rounded-lg p-2 text-[10px] text-zinc-300 focus:outline-none"
                    >
                      <option value="09:00 AM">09:00 AM</option>
                      <option value="12:00 PM">12:00 PM</option>
                      <option value="03:00 PM">03:00 PM</option>
                      <option value="06:00 PM">06:00 PM</option>
                      <option value="09:00 PM">09:00 PM</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => setShowAddScheduleModal(false)}
                    className="flex-1 py-2 bg-zinc-900 border border-zinc-700 text-zinc-300 rounded-lg text-[10px] uppercase font-bold hover:bg-zinc-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddSchedule}
                    className="flex-1 py-2 bg-purple-600 border border-purple-500/30 text-white rounded-lg text-[10px] uppercase font-bold hover:bg-purple-500"
                  >
                    Add Schedule
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* 5. PUBLISHING QUEUE */}
        <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-3xl p-5 flex flex-col gap-4 text-left hover:border-purple-500/30 shadow-[0_4px_30px_rgba(0,0,0,0.5)] transition-all">
          <div className="flex justify-between items-center border-b border-purple-500/10 pb-3">
            <div className="space-y-0.5">
              <span className="text-[8px] font-mono text-[#00e5ff] font-bold uppercase tracking-wider block">MODULE_05 // PUSH_INTERFACES</span>
              <h4 className="text-sm font-extrabold text-white flex items-center gap-1.5 uppercase tracking-wide">
                <Send className="w-4 h-4 text-purple-400 animate-pulse" />
                Publishing Queue
              </h4>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-1 bg-black/40 p-1 border border-white/5 rounded-xl text-center font-mono text-[9px] font-bold">
            {["Queue", "Drafts", "Published"].map((tab) => (
              <button
                key={tab}
                onClick={() => {
                  setPublishingQueueTab(tab as any);
                  triggerToast(`Publishing scope filtered: ${tab}`, "info");
                }}
                className={`py-1.5 rounded-lg transition-all ${
                  publishingQueueTab === tab ? "bg-[#9d4edd]/20 text-[#00e5ff] border border-purple-500/30" : "text-slate-500 hover:text-slate-350"
                }`}
              >
                {tab.toUpperCase()}
              </button>
            ))}
          </div>

          {/* Queue Items list container */}
          <div className="space-y-2.5 flex-1 max-h-[220px] overflow-y-auto custom-scrollbar pr-1">
            {queueItems.map((item) => (
              <div key={item.id} className="bg-black/30 border border-white/5 p-2 rounded-2xl flex items-center gap-3 relative font-mono">
                <div className="w-12 h-10 rounded-lg bg-purple-950/40 border border-purple-500/20 flex items-center justify-center relative shadow-inner overflow-hidden shrink-0">
                  {getPlatformIcon(item.platform, "w-5 h-5")}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <span className="text-[10px] font-bold text-white truncate block">{item.title}</span>
                  <div className="flex items-center gap-2 mt-0.5 text-[8px] text-zinc-500 uppercase tracking-widest font-black">
                    <span>{item.time}</span>
                    <span>•</span>
                    <span className="text-cyan-400">{item.views}</span>
                  </div>
                </div>
                
                {/* Custom glowing status indicators requested */}
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                  <span className={`text-[7px] font-mono px-1.5 py-0.5 rounded-md border font-extrabold uppercase ${
                    item.status === "Scheduled" ? "bg-indigo-950/40 border-indigo-500/30 text-indigo-300" :
                    item.status === "Rendering" ? "bg-amber-950/40 border-amber-500/30 text-amber-300 animate-pulse" :
                    item.status === "Optimizing" ? "bg-purple-950/40 border-purple-500/30 text-purple-300 animate-pulse" :
                    item.status === "Draft" ? "bg-slate-900 border-zinc-700 text-zinc-400" :
                    item.status === "Published" ? "bg-emerald-950/30 border-emerald-500/30 text-emerald-400" :
                    "bg-cyan-950/40 border-cyan-500/30 text-cyan-300"
                  }`}>
                    {item.status}
                  </span>
                  {item.status !== "Published" && (
                    <button
                      onClick={() => handlePublishNow(item.id)}
                      className="px-2 py-0.5 bg-[#00e5ff] hover:bg-white text-black font-sans font-extrabold text-[8.5px] rounded transition-all active:scale-95 cursor-pointer uppercase tracking-tight"
                    >
                      Publish
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={() => {
              triggerToast("Bulked immediate publish command triggered!", "purple");
              queueItems.forEach(item => {
                if (item.status !== "Published") {
                  handlePublishNow(item.id);
                }
              });
            }}
            className="w-full py-2 bg-gradient-to-r from-[#00e5ff] to-purple-600 text-black font-sans font-black text-xs uppercase tracking-widest rounded-xl transition-all shadow-[0_0_15px_rgba(0,229,255,0.25)] hover:from-white hover:to-cyan-400 cursor-pointer active:scale-95 text-center block mt-2"
          >
            Publish Content Now ({queueItems.filter(i => i.status !== "Published").length})
          </button>
        </div>

        {/* 6. ANALYTICS */}
        <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-3xl p-5 flex flex-col gap-4 text-left hover:border-purple-500/30 shadow-[0_4px_30px_rgba(0,0,0,0.5)] transition-all">
          <div className="flex justify-between items-center border-b border-purple-500/10 pb-3">
            <div className="space-y-0.5">
              <span className="text-[8px] font-mono text-cyan-400 font-bold uppercase tracking-wider block">MODULE_06 // PERFORMANCE_DASH</span>
              <h4 className="text-sm font-extrabold text-white flex items-center gap-1.5 uppercase tracking-wide">
                <TrendingUp className="w-4 h-4 text-purple-400 animate-pulse" />
                Performance Analytics
              </h4>
            </div>
            <select
              value={analyticsCycle}
              onChange={(e) => {
                setAnalyticsCycle(e.target.value as any);
                triggerToast(`Analytical spectrum adjusted to: ${e.target.value}`, "info");
              }}
              className="bg-black/60 text-zinc-400 border border-white/10 rounded-lg p-1 px-1.5 focus:outline-none text-[8.5px] font-mono font-bold uppercase tracking-wider"
            >
              <option value="This Cycle">This Cycle</option>
              <option value="All Time">All Time Spectrum</option>
            </select>
          </div>

          <p className="text-zinc-400 text-[11px] leading-normal font-sans">
            Secure tracking of continuous engagement harvests and client-conversion trajectory indices.
          </p>

          {/* Recharts Area Performance Graph */}
          <div className="w-full h-[120px] bg-black/30 border border-white/5 rounded-2xl p-1 relative overflow-hidden">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={ENGAGEMENT_CHART_DATA} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                <defs>
                  <linearGradient id="purpleGlow" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#9d4edd" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#9d4edd" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" stroke="#64748b" fontSize={7} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748b" fontSize={7} tickLine={false} axisLine={false} />
                <Tooltip 
                  contentStyle={{ backgroundColor: "#060416", borderColor: "#9d4edd", fontSize: 9, fontFamily: "monospace" }}
                  itemStyle={{ color: "#00e5ff" }}
                />
                <Area type="monotone" dataKey="engagement" stroke="#9d4edd" strokeWidth={2} fillOpacity={1} fill="url(#purpleGlow)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Platform Performance metrics breakdown */}
          <div className="space-y-2 flex-1">
            <span className="text-[8px] font-mono text-purple-400 block uppercase tracking-widest font-black">
              ENGAGEMENT REACH BREAKDOWN BY PLATFORM
            </span>
            <div className="space-y-1.5 font-mono text-[9px]">
              {[
                { name: "YouTube", reach: "12.4K reach", color: "text-emerald-400", change: "+35%" },
                { name: "Instagram", reach: "14.8K reach", color: "text-emerald-400", change: "+42%" },
                { name: "TikTok", reach: "9.7K reach", color: "text-emerald-400", change: "+31%" },
                { name: "LinkedIn", reach: "7.2K reach", color: "text-emerald-400", change: "+25%" },
                { name: "Blog", reach: "4.6K reach", color: "text-emerald-400", change: "+18%" }
              ].map(platform => (
                <div key={platform.name} className="flex justify-between items-center bg-black/25 p-1 px-2 rounded-lg border border-white/5">
                  <div className="flex items-center gap-1.5">
                    {getPlatformIcon(platform.name, "w-3 h-3")}
                    <span className="text-[9.5px] font-semibold text-slate-300">{platform.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-400 text-[9px]">{platform.reach}</span>
                    <span className={`text-[9px] font-bold ${platform.color}`}>{platform.change}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 7. BRAND VOICE & ASSETS */}
        <div className="bg-[#120f26]/40 backdrop-blur-md border border-purple-500/20 rounded-3xl p-5 flex flex-col gap-4 text-left hover:border-purple-500/30 shadow-[0_4px_30px_rgba(0,0,0,0.5)] transition-all">
          <div className="flex justify-between items-center border-b border-purple-500/10 pb-3">
            <div className="space-y-0.5">
              <span className="text-[8px] font-mono text-cyan-400 font-bold uppercase tracking-wider block">MODULE_07 // BRAND_SYNAPSE</span>
              <h4 className="text-sm font-extrabold text-white flex items-center gap-1.5 uppercase tracking-wide">
                <Volume2 className="w-4 h-4 text-purple-400 animate-pulse" />
                Brand Voice & Assets
              </h4>
            </div>
          </div>

          <div className="bg-black/30 p-2.5 rounded-2xl border border-white/5 space-y-2 text-[10px] font-mono">
            <div className="flex justify-between items-center pb-1">
              <span className="text-zinc-500 uppercase font-black text-[8.5px]">1. ACTIVE VOICE PROFILE</span>
              <button 
                onClick={() => {
                  setIsEditingBrandVoice(!isEditingBrandVoice);
                  if (isEditingBrandVoice) triggerToast("Brand profile synchronized with LLM core.", "success");
                }}
                className="text-cyan-400 hover:text-white text-[8.5px] font-bold uppercase tracking-wider"
              >
                {isEditingBrandVoice ? "Save Sync" : "Edit Profile"}
              </button>
            </div>
            {isEditingBrandVoice ? (
              <input
                type="text"
                value={brandVoiceProfile}
                onChange={(e) => setBrandVoiceProfile(e.target.value)}
                className="w-full bg-black/60 border border-purple-400/40 rounded p-1 text-[10px] text-[#00e5ff] font-bold"
              />
            ) : (
              <div className="text-white font-extrabold text-xs uppercase flex items-center gap-1.5 pb-1">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping"></span>
                <span>{brandVoiceProfile}</span>
              </div>
            )}
            <p className="text-[9px] text-zinc-400 font-sans normal-case">
              Maintains strict narrative compliance across physical node operators (Communications Associate, Staff Coordinator), matching official corporate guidelines.
            </p>
          </div>

          {/* Tone profile badges selectable */}
          <div className="space-y-1.5">
            <span className="text-[8px] font-mono text-purple-400 block uppercase tracking-widest font-black">
              2. ACTIVE BRAND TONE VECTOR INDICES
            </span>
            <div className="flex flex-wrap gap-1.5">
              {brandTones.map((tone, idx) => (
                <button
                  key={tone.name}
                  onClick={() => {
                    toggleBrandTone(idx);
                    triggerToast(`Adjusted relevance coefficient of tone: ${tone.name}`, "info");
                  }}
                  className={`px-2.5 py-1 rounded-full text-[9px] font-mono font-bold border transition-all cursor-pointer ${
                    tone.active
                      ? "bg-purple-900/30 border-purple-500/40 text-cyan-400 font-black shadow-[0_0_8px_rgba(157,78,221,0.25)]"
                      : "bg-black/30 border-white/5 text-zinc-500"
                  }`}
                >
                  {tone.name}
                </button>
              ))}
            </div>
          </div>

          {/* Asset library drawer list */}
          <div className="space-y-1.5 flex-1 mt-2">
            <span className="text-[8px] font-mono text-cyan-300 block uppercase tracking-widest font-black">
              3. NEXORAX NexoraX Resource Library ASSETS
            </span>
            <div className="grid grid-cols-2 gap-2 text-[9px] font-mono">
              {[
                { label: "Videos", count: "128 assets", size: "4.2 GB" },
                { label: "Images", count: "342 assets", size: "1.1 GB" },
                { label: "Audio", count: "86 accounts", size: "320 MB" },
                { label: "Documents", count: "54 files", size: "15.4 MB" }
              ].map(asset => (
                <div key={asset.label} className="bg-black/40 border border-white/5 p-2 rounded-xl text-left hover:border-purple-500/25 transition-all">
                  <span className="text-slate-300 font-black uppercase text-[9.5px] block">{asset.label}</span>
                  <div className="flex justify-between items-center text-[8px] text-zinc-500 mt-1">
                    <span>{asset.count}</span>
                    <span className="text-[#00e5ff] font-bold">{asset.size}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>

      {/* BOTTOM FEATURE STRIP */}
      <div className="w-full bg-[#120f26]/20 border border-purple-500/10 rounded-2xl p-4 mt-8 grid grid-cols-2 md:grid-cols-5 gap-4 shadow-inner backdrop-blur-sm text-left">
        {[
          { label: "Multi-Channel Publishing", desc: "YouTube, Instagram, TikTok, LinkedIn, Blog" },
          { label: "AI-Powered Creation", desc: "Topic idea to script, visuals, voice and captions" },
          { label: "Smart Scheduling", desc: "Best times, auto-optimize and queue distributions" },
          { label: "Content Batching", desc: "Plan once, create in bulk, publish on autopilot" },
          { label: "Performance Insights", desc: "Track growth, engagement and lead generations" }
        ].map((item, idx) => (
          <div key={idx} className="space-y-1 border-l border-purple-500/15 pl-3">
            <span className="text-[10px] font-bold text-white flex items-center gap-1.5 uppercase font-sans tracking-wide">
              <Check className="w-3.5 h-3.5 text-cyan-400" />
              {item.label}
            </span>
            <p className="text-[9px] text-zinc-400 font-medium font-sans mt-1 leading-normal normal-case">{item.desc}</p>
          </div>
        ))}
      </div>

      {/* BOTTOM CTA BANNER */}
      <div className="relative mt-8 rounded-3xl border border-purple-500/30 bg-gradient-to-r from-[#170e30] via-[#090518] to-[#170e30] p-6 overflow-hidden group shadow-[0_0_40px_rgba(157,78,221,0.15)] select-none">
        <div className="absolute inset-0 bg-[radial-gradient(#9d4edd_1px,transparent_1px)] [background-size:20px_20px] opacity-10 pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-1">
            <h4 className="text-base font-sans font-black text-white uppercase tracking-tight">More Content. Less Effort. Bigger Impact.</h4>
            <p className="text-zinc-400 text-xs font-sans max-w-xl normal-case leading-normal font-medium">
              Maximize your throughput without compromising visual fidelity. The ultimate SaaS orchestration portal for digital dominance.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <span className="text-[#00e5ff] font-mono text-xs font-bold uppercase tracking-[0.25em] animate-pulse">
              Automate. Scale. Dominate.
            </span>
            <div className="text-3xl md:text-4xl font-extrabold text-[#9d4edd] font-sans tracking-widest hidden sm:block">X</div>
          </div>
        </div>
      </div>
    </div>
  );
}


function CampaignIntelligenceHub() {
  const campaignMetrics = [
    { label: "TikTok Views", value: "55.7K+", note: "Campaign 2 live audience", glow: "from-cyan-400/20 to-blue-500/10" },
    { label: "Ad Spend", value: "£15.46", note: "Paid test spend tracked", glow: "from-emerald-400/20 to-cyan-500/10" },
    { label: "Smart Quote Visits", value: "8,923", note: "UTM traffic detected", glow: "from-purple-400/20 to-fuchsia-500/10" },
    { label: "Destination CTR", value: "0.40%", note: "TikTok destination response", glow: "from-amber-400/20 to-orange-500/10" },
  ];

  const journey = [
    ["01", "Video View", "TikTok / Reels / Shorts audience sees the campaign."],
    ["02", "Tracked Click", "UTM link sends traffic into Smart Quote or Starter Offer."],
    ["03", "Lead Action", "WhatsApp, callback, quote form or preview request is recorded."],
    ["04", "Sales Follow-Up", "Mahmood, Anjum or Nazia can turn attention into income."],
  ];

  return (
    <section className="xl:col-span-2 relative overflow-hidden rounded-[2rem] border border-cyan-400/25 bg-black/35 backdrop-blur-2xl p-5 md:p-7 shadow-[0_0_60px_rgba(34,211,238,0.14)]">
      <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-emerald-500/5 pointer-events-none" />
      <div className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-cyan-400/20 blur-3xl animate-pulse pointer-events-none" />
      <div className="absolute -bottom-28 -left-20 h-72 w-72 rounded-full bg-fuchsia-500/20 blur-3xl animate-pulse pointer-events-none" />

      <div className="relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-6">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-cyan-300 font-black">
              NexoraX Campaign Intelligence Hub
            </p>
            <h2 className="mt-3 text-3xl md:text-5xl font-black text-white uppercase tracking-tight">
              Track every advert from <span className="text-cyan-300">video view</span> to <span className="text-emerald-300">paid lead.</span>
            </h2>
            <p className="mt-3 max-w-3xl text-sm md:text-base text-slate-300 leading-relaxed">
              A free campaign command centre included with NexoraX websites, dashboards, QR campaigns and launch packs.
              Track TikTok, Smart Quote visits, WhatsApp clicks, callback requests and sales follow-up from one place.
            </p>
          </div>

          <div className="rounded-2xl border border-emerald-400/25 bg-emerald-950/20 px-5 py-4 text-left min-w-[220px]">
            <p className="text-[9px] font-mono uppercase tracking-[0.25em] text-emerald-300 font-black">Offer Hook</p>
            <p className="mt-2 text-2xl font-black text-white">Free Tracker</p>
            <p className="text-xs text-slate-300">Included with every build package</p>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {campaignMetrics.map((metric) => (
            <div key={metric.label} className={`rounded-2xl border border-white/10 bg-gradient-to-br ${metric.glow} p-4`}>
              <p className="text-[9px] uppercase tracking-[0.22em] text-slate-400 font-mono font-black">{metric.label}</p>
              <p className="mt-2 text-2xl md:text-3xl font-black text-white">{metric.value}</p>
              <p className="mt-1 text-[11px] text-slate-400">{metric.note}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-6">
          {journey.map((step) => (
            <div key={step[0]} className="rounded-2xl border border-cyan-400/15 bg-black/35 p-4 hover:border-cyan-300/40 transition-all">
              <div className="h-8 w-8 rounded-full border border-cyan-300/30 bg-cyan-500/10 flex items-center justify-center text-cyan-200 font-black text-xs mb-3">
                {step[0]}
              </div>
              <p className="text-sm font-black text-white uppercase">{step[1]}</p>
              <p className="mt-2 text-xs text-slate-400 leading-relaxed">{step[2]}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          <a
            href="/smart-quote?utm_source=tiktok&utm_medium=paid_social&utm_campaign=tiktok_smart_quote_traffic_test&utm_content=smart_quote_explainer"
            target="_blank"
            rel="noreferrer"
            className="rounded-xl border border-cyan-400/40 bg-cyan-500/10 px-4 py-3 text-xs font-black uppercase tracking-wider text-cyan-100 hover:bg-cyan-400/20 transition-all"
          >
            Test Smart Quote Tracking URL
          </a>

          <a
            href="/campaign-launch-pad"
            target="_blank"
            rel="noreferrer"
            className="rounded-xl border border-purple-400/40 bg-purple-500/10 px-4 py-3 text-xs font-black uppercase tracking-wider text-purple-100 hover:bg-purple-400/20 transition-all"
          >
            Open Launch Pad
          </a>

          <a
            href="/remote-workplace"
            target="_blank"
            rel="noreferrer"
            className="rounded-xl border border-emerald-400/40 bg-emerald-500/10 px-4 py-3 text-xs font-black uppercase tracking-wider text-emerald-100 hover:bg-emerald-400/20 transition-all"
          >
            Open Follow-Up Board
          </a>
        </div>
      </div>
    </section>
  );
}
